import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Avatar, Button, Card, Col, Descriptions, Empty, Form, Input, message, Modal, Popconfirm, Row, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  LinkOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { api, AUTH_USERNAME_KEY } from "../services/apiClient";
import { canUseUserFilter, fetchUserOptions, userQuery } from "../utils/adminUserFilter";

const { Title, Text } = Typography;
const { TextArea } = Input;

const CHANNEL_STATUS = {
  ACTIVE: "Hoạt động",
  DISCONNECTED: "Mất kết nối",
  ERROR: "Lỗi",
};

function statusLabel(value) {
  return CHANNEL_STATUS[value] || CHANNEL_STATUS.DISCONNECTED;
}

function formatDateTime(value) {
  if (!value) return "Chưa có";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Chưa có";
  return date.toLocaleString("vi-VN");
}

function formatRefreshTokenExpiry(item) {
  if (item.refreshTokenExpiresAt) return formatDateTime(item.refreshTokenExpiresAt);
  if (item.refreshToken) return "Không có thời hạn cố định";
  return "Chưa có";
}

const detailLabels = {
  channelId: "YouTube Channel ID",
  title: "Tiêu đề kênh",
  description: "Mô tả",
  customUrl: "Custom URL",
  publishedAt: "Ngày tạo",
  country: "Quốc gia",
  thumbnailUrl: "Ảnh kênh",
  subscriberCount: "Người đăng ký",
  viewCount: "Tổng lượt xem",
  videoCount: "Số video",
  hiddenSubscriberCount: "Ẩn số subscriber",
  brandingTitle: "Tên branding",
  keywords: "Từ khóa",
  unsubscribedTrailer: "Trailer",
  defaultLanguage: "Ngôn ngữ mặc định",
  trackingAnalyticsAccountId: "Analytics ID",
  uploadsPlaylistId: "Playlist uploads",
  likesPlaylistId: "Playlist likes",
  privacyStatus: "Quyền riêng tư",
  isLinked: "Đã liên kết",
  longUploadsStatus: "Upload dài",
  madeForKids: "Made for kids",
  topicIds: "Topic IDs",
  topicCategories: "Topic categories",
  channelName: "Tên quản lý",
  credentialMode: "Credential",
  status: "Trạng thái",
  accessTokenExpiresAt: "Token hết hạn",
  refreshTokenExpiresAt: "Refresh token hết hạn",
};

function formatDetailValue(key, value) {
  if (value === null || value === undefined || value === "") return null;
  if (key === "status") return statusLabel(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Có" : "Không";
  if (["subscriberCount", "viewCount", "videoCount"].includes(key)) return Number(value).toLocaleString("vi-VN");
  if (["publishedAt", "accessTokenExpiresAt"].includes(key)) return formatDateTime(value);
  return String(value);
}

function getChannelStat(item, key) {
  const value = item?.youtubeDetails?.[key] ?? item?.[key];
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("vi-VN") : String(value);
}

function buildChannelDetails(item) {
  if (item.youtubeDetails && Object.keys(item.youtubeDetails).length) {
    return { ...item.youtubeDetails, channelName: item.channelName, status: item.status };
  }

  const isPrivate = Boolean(item.clientId);
  return {
    title: item.channelTitle || item.channelName,
    channelName: item.channelName,
    channelId: item.channelId,
    thumbnailUrl: item.thumbnailUrl,
    subscriberCount: item.subscriberCount,
    status: item.status || "DISCONNECTED",
    credentialMode: isPrivate ? "Credential riêng" : "Credential hệ thống",
    accessTokenExpiresAt: item.accessTokenExpiresAt,
    refreshTokenExpiresAt: formatRefreshTokenExpiry(item),
  };
}

export default function AutoChannels() {
  const [items, setItems] = useState([]);
  const [currentUserId, setCurrentUserId] = useState();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [syncDetails, setSyncDetails] = useState(null);
  const [privateCredentialGuideUrl, setPrivateCredentialGuideUrl] = useState("");
  const [credentialOptions, setCredentialOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [form] = Form.useForm();
  const location = useLocation();
  const showUserFilter = canUseUserFilter();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.get(`/api/channels${userQuery(selectedUserId)}`)) || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    load();
    api.get("/api/channels/private-credential-guide")
      .then((result) => setPrivateCredentialGuideUrl(result?.url || ""))
      .catch(() => {});
  }, [load]);

  useEffect(() => {
    api.get("/api/auth/me").then((profile) => setCurrentUserId(profile?.id)).catch(() => {});
    api.get("/api/system/youtube-credentials/available").then((options) => setCredentialOptions(options || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!showUserFilter) return;
    fetchUserOptions().then(setUsers).catch((error) => message.error(error.message));
  }, [showUserFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const oauthStatus = params.get("status");
    if (oauthStatus === "success") message.success("Đã kết nối kênh YouTube");
    if (oauthStatus === "error") message.error("Kết nối kênh YouTube thất bại");
  }, [location.search]);

  const data = useMemo(
    () => items.filter((item) => (
      (status === "ALL" || item.status === status) &&
      `${item.channelName || ""} ${item.channelTitle || ""} ${item.channelId || ""} ${item.clientId || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )),
    [items, search, status]
  );

  const closeModal = () => {
    setOpen(false);
    setEditingChannel(null);
    form.resetFields();
  };

  const openCreateModal = () => {
    setEditingChannel(null);
    form.setFieldsValue({ mode: "PRIVATE", credentialId: undefined, clientId: "", clientSecret: "" });
    setOpen(true);
  };

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    form.setFieldsValue({
      channelName: channel.channelName,
      channelId: channel.channelId,
      otherInfo: channel.otherInfo || "",
      openProfileUrl: channel.openProfileUrl || "",
      mode: channel.clientId ? "PRIVATE" : "MASTER",
      credentialId: channel.credentialId,
      clientId: channel.clientId || "",
      clientSecret: "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        channelName: values.channelName,
        channelId: values.channelId,
        otherInfo: values.otherInfo || null,
        openProfileUrl: values.openProfileUrl || null,
        credentialMode: values.mode,
        credentialId: values.mode === "MASTER" ? values.credentialId : null,
        clientId: values.mode === "PRIVATE" ? values.clientId : null,
        clientSecret: values.mode === "PRIVATE" ? values.clientSecret || null : null,
      };

      if (editingChannel) {
        await api.put(`/api/channels/${editingChannel.id}`, payload);
        message.success("Đã cập nhật kênh");
      } else {
        await api.post("/api/channels", payload);
        message.success("Đã thêm kênh");
      }
      closeModal();
      load();
    } catch (error) {
      if (error.message) message.error(error.message);
    }
  };

  const connect = async (id) => {
    try {
      const url = await api.get(`/api/oauth/connect/${id}`);
      window.location.assign(url);
    } catch (error) {
      message.error(error.message);
    }
  };

  const copyConnectLink = async (id) => {
    try {
      const url = await api.get(`/api/oauth/connect-link/${id}`);
      await navigator.clipboard.writeText(url);
      message.success("Đã copy link kết nối");
    } catch (error) {
      message.error(error.message);
    }
  };

  const sync = async (id) => {
    try {
      const result = await api.get(`/api/channels/sync/${id}`);
      if (result?.channel) {
        setItems((current) => current.map((item) => (item.id === result.channel.id ? result.channel : item)));
      }
      message.success("Đã đồng bộ kênh");
      load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const showDetails = (item) => setSyncDetails(buildChannelDetails(item));

  const openProfile = async (item) => {
    const profileUrl = item.openProfileUrl?.trim();
    if (!profileUrl) {
      message.warning("Kênh chưa có URL Open profile (GPMLogin)");
      return;
    }

    try {
      const parsedUrl = new URL(profileUrl);
      if (!["localhost", "127.0.0.1", "::1"].includes(parsedUrl.hostname)) {
        throw new Error("URL Open profile phải là API localhost");
      }
      await window.fetch(parsedUrl.toString(), { method: "GET", mode: "no-cors", cache: "no-store" });
      message.success("Đã gửi lệnh mở profile");
    } catch (error) {
      message.error(error.message || "Không thể gọi API mở profile");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/channels/${id}`);
      setItems((current) => current.filter((item) => item.id !== id));
      message.success("Đã xóa kênh");
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: "KÊNH",
      render: (_, item) => {
        const ownerOption = users.find((user) => user.value === item.userId);
        const owner = ownerOption?.username || ownerOption?.label
          || (item.userId === currentUserId ? localStorage.getItem(AUTH_USERNAME_KEY) : null);
        return (
        <Space size={12}>
          {item.channelId ? (
            <a className="auto-channel-icon" href={`https://www.youtube.com/channel/${item.channelId}`} target="_blank" rel="noreferrer" title="Mở kênh YouTube">
              {item.thumbnailUrl ? <Avatar size={36} src={item.thumbnailUrl} /> : <YoutubeOutlined />}
            </a>
          ) : <span className="auto-channel-icon">{item.thumbnailUrl ? <Avatar size={36} src={item.thumbnailUrl} /> : <YoutubeOutlined />}</span>}
          <div>
            <Space size={6}>
              <Text strong>{item.channelTitle || item.channelName}</Text>
              <Tooltip title="Đồng bộ kênh">
                <Button aria-label="Đồng bộ kênh" className="auto-inline-sync" icon={<SyncOutlined />} onClick={(event) => { event.stopPropagation(); sync(item.id); }} type="text" />
              </Tooltip>
            </Space>
            <br />
            <Text type="secondary">{item.channelId || "Chưa kết nối"}</Text>
            {owner && <><br /><Text type="secondary">[{owner}]</Text></>}
          </div>
        </Space>
        );
      },
    },
    {
      title: "CREDENTIAL",
      render: (_, item) => {
        const isPrivate = item.credentialMode === "PRIVATE" || Boolean(item.clientId);
        return (
          <div className="auto-credential-cell">
            <Tag icon={<KeyOutlined />} color={isPrivate ? "purple" : "blue"}>{isPrivate ? "Credential riêng" : "Credential hệ thống"}</Tag>
            <Text type="secondary">Token hết hạn: {formatDateTime(item.accessTokenExpiresAt)}</Text>
            <Text type="secondary">Refresh token hết hạn: {formatRefreshTokenExpiry(item)}</Text>
          </div>
        );
      },
    },
    {
      title: "TRẠNG THÁI",
      render: (_, item) => <Tag color={item.status === "ACTIVE" ? "green" : item.status === "ERROR" ? "red" : "orange"}>{statusLabel(item.status)}</Tag>,
    },
    { title: "NGƯỜI ĐĂNG KÝ", dataIndex: "subscriberCount", render: (value) => value?.toLocaleString("vi-VN") || "—" },
    { title: "SỐ VIDEO", render: (_, item) => getChannelStat(item, "videoCount") || "?" },
    { title: "TỔNG LƯỢT XEM", render: (_, item) => getChannelStat(item, "viewCount") || "?" },
    {
      title: "THAO TÁC",
      render: (_, item) => {
        const canManage = !item.userId || item.userId === currentUserId;
        return (
        <div className="provider-actions auto-channel-actions">
          <span><Tooltip title="Kết nối"><Button aria-label="Kết nối" type="text" className="provider-action-button" icon={<LinkOutlined />} disabled={item.status === "ACTIVE"} onClick={() => connect(item.id)} /></Tooltip></span>
          <span><Tooltip title="Mở profile"><Button aria-label="Mở profile" type="text" className="provider-action-button" icon={<PlayCircleOutlined />} disabled={!item.openProfileUrl} onClick={() => openProfile(item)} /></Tooltip></span>
          <span><Tooltip title={canManage ? "Sửa kênh" : "Chỉ chủ kênh mới được sửa"}><Button aria-label="Sửa kênh" type="text" className="provider-action-button" icon={<EditOutlined />} disabled={!canManage} onClick={() => openEditModal(item)} /></Tooltip></span>
          <span><Tooltip title={canManage ? "Copy link kết nối" : "Chỉ chủ kênh mới được copy link kết nối"}><Button aria-label="Copy link kết nối" type="text" className="provider-action-button" icon={<CopyOutlined />} disabled={!canManage} onClick={() => copyConnectLink(item.id)} /></Tooltip></span>
          <span><Tooltip title="Xem chi tiết"><Button aria-label="Xem chi tiết" type="text" className="provider-action-button" icon={<InfoCircleOutlined />} onClick={() => showDetails(item)} /></Tooltip></span>
          <span><Popconfirm title="Xóa kênh này?" onConfirm={() => remove(item.id)}><Tooltip title={canManage ? "Xóa kênh" : "Chỉ chủ kênh mới được xóa"}><Button aria-label="Xóa kênh" type="text" danger className="provider-action-button" icon={<DeleteOutlined />} disabled={!canManage} /></Tooltip></Popconfirm></span>
        </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Kênh YouTube</Title>
          <Text type="secondary">Kết nối và quản lý các kênh dùng để Auto Upload.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm tên, Channel ID hoặc Client ID" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Select value={status} onChange={setStatus} options={["ALL", "ACTIVE", "DISCONNECTED", "ERROR"].map((value) => ({ value, label: value === "ALL" ? "Tất cả trạng thái" : statusLabel(value) }))} />
          {showUserFilter && <Select allowClear showSearch optionFilterProp="label" placeholder="Tìm theo username" style={{ minWidth: 240 }} value={selectedUserId} onChange={setSelectedUserId} options={users} />}
          <Button icon={<ReloadOutlined />} onClick={load}>Làm mới</Button>
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Thêm kênh</Button>
        </div>
        <Table className="auto-channel-table" rowKey="id" loading={loading} columns={columns} dataSource={data} tableLayout="fixed" />
      </Card>
      <Modal title={editingChannel ? "Sửa kênh YouTube" : "Thêm kênh YouTube"} visible={open} onCancel={closeModal} onOk={save} okText="Lưu" width={960}>
        <Form form={form} layout="vertical" initialValues={{ mode: "PRIVATE" }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="channelName" label="Tên quản lý" rules={[{ required: true, message: "Nhập tên quản lý" }]}><Input /></Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="channelId" label="YouTube Channel ID" rules={[{ required: true, message: "Nhập YouTube Channel ID" }]}><Input /></Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="mode" label="Credential">
                <Select options={[{ value: "MASTER", label: "Dùng credential hệ thống" }, { value: "PRIVATE", label: "Credential riêng" }]} />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => form.getFieldValue("mode") === "PRIVATE" && (
                  <>
                    <Form.Item name="clientId" label="Client ID" rules={[{ required: true, message: "Nhập Client ID" }]}><Input.TextArea rows={3} /></Form.Item>
                    <Form.Item name="clientSecret" label={editingChannel ? "Client Secret mới" : "Client Secret"} extra={editingChannel ? "Để trống nếu không muốn đổi Client Secret hiện tại." : undefined} rules={editingChannel ? [] : [{ required: true, message: "Nhập Client Secret" }]}><Input.TextArea rows={3} /></Form.Item>
                    {privateCredentialGuideUrl && <Alert className="mb-16" message={<a href={privateCredentialGuideUrl} target="_blank" rel="noreferrer">Xem video hướng dẫn lấy Client ID và Client Secret</a>} showIcon type="info" />}
                  </>
                )}
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => form.getFieldValue("mode") === "MASTER" && (
                  <Form.Item name="credentialId" label="Credential đã cấu hình" rules={[{ required: true, message: "Chọn credential" }]}>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Chọn credential đã cấu hình"
                      options={credentialOptions
                        .map((credential) => ({
                          value: credential.id,
                          label: `${credential.clientId} (${credential.ownerName})`,
                        }))}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="otherInfo" label="Thông tin khác"><TextArea rows={10} /></Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="openProfileUrl" label="Open profile (GPMLogin)">
                <Input placeholder="http://127.0.0.1:19999/api/v3/profiles/start/..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal title="Thông tin chi tiết kênh" visible={Boolean(syncDetails)} onCancel={() => setSyncDetails(null)} footer={[<Button key="close" type="primary" onClick={() => setSyncDetails(null)}>Đóng</Button>]} width={820}>
        {syncDetails && Object.keys(syncDetails).length ? (
          <Descriptions bordered column={1} size="small">
            {Object.entries(syncDetails).map(([key, value]) => [key, formatDetailValue(key, value)]).filter(([, value]) => value).map(([key, value]) => (
              <Descriptions.Item key={key} label={detailLabels[key] || key}>
                {key === "thumbnailUrl" ? <a href={value} target="_blank" rel="noreferrer">{value}</a> : value}
              </Descriptions.Item>
            ))}
          </Descriptions>
        ) : <Empty description="Không có thông tin bổ sung" />}
      </Modal>
    </>
  );
}
